import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { errorTost } from "../../Services/Toast";

const steps = [
  { label: "Project Scope", sub: "Select projects" },
  { label: "Brokerage Type", sub: "Choose calculation" },
  { label: "Status", sub: "Set status" },
  { label: "Review", sub: "Confirm & save" },
];

const CreateBasicBrokeragePlanStep3 = ({
  data,
  setData,
  onBack,
  onNext,
}) => {
  const { startDate, endDate, isActive } = data;

  const handleNextClick = () => {
    if (!data.startDate) {
      errorTost("dates are required")
      return;
    }

    if (data.endDate && data.endDate < data.startDate) {
      errorTost("End date cannot be before start date");
      return;
    }

    const status = data.isActive ? "Active" : "Inactive";

    setData(prev => ({
      ...prev,
      start_date: prev.startDate,
      end_date: prev.endDate || null,
      status,
    }));

    onNext();
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1}}>

      {/* TOP BAR */}
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ color: "#2563EB", textTransform: "none" }}
          onClick={onBack}
        >
          Back to Plans
        </Button>
      </Box>

      {/* STEPPER */}
      <Stepper
        activeStep={2}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepIcon-root": { fontSize: "3rem" },
          "& .MuiStepIcon-root.Mui-active": { color:  "#2563EB" },
          "& .MuiStepIcon-root.Mui-completed": { color:  "#2563EB" },
          "& .MuiStepLabel-label": {
            fontSize: "15px",
            fontWeight: 600,
            mt: 1,
          },
          "& .MuiStepConnector-line": {
            borderTopWidth: 3,
            borderColor: "#E5E7EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography fontWeight={600}>{step.label}</Typography>
              <Typography fontSize={13} color="text.secondary">
                {step.sub}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* MAIN GRID */}
      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={4}>

        {/* LEFT SECTION */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 4,
            border: "1px solid #E5E7EB",
          }}
        >
          <Box display="flex" gap={1.5} alignItems="center" mb={3}>
            <CalendarTodayOutlinedIcon sx={{ color: "#2563EB" }} />
            <Typography fontSize={18} fontWeight={600}>
              status
            </Typography>
          </Box>

          {/* DATE FIELDS */}
          {/* <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3} mb={4}>
            <Box>
              <Typography fontWeight={500} mb={1}>
                Start Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={startDate || ""}
                onChange={(e) =>
                  setData({ ...data, startDate: e.target.value })
                }
              />
            </Box>

            <Box>
              <Typography fontWeight={500} mb={1}>
                End Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={endDate || ""}
                onChange={(e) =>
                  setData({ ...data, endDate: e.target.value })
                }
              />
            </Box>
          </Box> */}

          {/* STATUS SECTION */}
          <Box
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid #E5E7EB",
              background: "#F8FAFC",
              mb: 3,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography fontWeight={600}>Status</Typography>
                <Typography fontSize={13} color="text.secondary">
                  Activate this configuration immediately
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) =>
                      setData({
                        ...data,
                        isActive: e.target.checked,
                      })
                    }
                  />
                }
                label=""
              />
            </Box>
          </Box>

          {/* INFO */}
          <Typography fontSize={13} color="text.secondary">
            ⚠️ Leave end date empty to apply indefinitely. Overlap validation will run before saving.
          </Typography>
        </Box>

        {/* RIGHT SECTION */}
        <Box display="flex" flexDirection="column" gap={3}>

          {/* LIVE PREVIEW */}
          {/* <Box
            sx={{
              background: "#fff",
              borderRadius: 3,
              p: 3,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography fontWeight={600} mb={2}>
              Live Preview
            </Typography>

            <Typography fontSize={14}>
              Agreement Value: <b>₹5.00 Cr</b>
            </Typography>

            <Typography fontSize={14}>
              Base Brokerage: <b>₹0.00 L</b>
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={600} fontSize={16}>
              Brokerage: ₹0.00 L
            </Typography>
          </Box> */}

          {/* BUSINESS RULES */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: 3,
              p: 3,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography fontWeight={600} mb={2}>
              Business Rules
            </Typography>

            {[
              "Calculated after agreement completion",
              "Paid immediately",
              "Cannot be overridden",
              "Always shown on CP dashboard",
            ].map((rule) => (
              <Box key={rule} display="flex" gap={1.5} mb={1.5}>
                <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                <Typography fontSize={13}>{rule}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box display="flex" justifyContent="space-between" mt={6}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleNextClick}
          sx={{
            background: "#4F6EF7",
            textTransform: "none",
            borderRadius: 2,
            px: 4,
          }}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreateBasicBrokeragePlanStep3;